import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { formatINR } from "@/lib/format";
import { Banknote, Calendar, Flag, TrendingUp, Flame, Repeat } from "lucide-react";

const STEPUPS = [0.05, 0.1, 0.15, 0.2];

function Field({ icon: Icon, label, hint, children }) {
  return (
    <div className="group space-y-2.5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-400">
        <Icon className="w-3.5 h-3.5 text-[#F97316]" />
        {label}
      </div>
      {children}
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export default function CalculatorSection({ inputs, setInputs }) {
  const set = (patch) => setInputs({ ...inputs, ...patch });

  const safeNum = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  return (
    <section
      aria-labelledby="calc-heading"
      className="rounded-2xl border border-white/10 bg-[#111827]/70 backdrop-blur-md p-6 sm:p-8 lg:p-10"
      data-testid="calculator-section"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#F97316]">
            Control Room
          </div>
          <h2
            id="calc-heading"
            className="mt-2 font-heading text-2xl sm:text-3xl font-semibold tracking-tight"
            data-testid="calculator-heading"
          >
            Your Inputs
          </h2>
        </div>
        <div className="num-font text-xs text-slate-500">
          Updates live • {inputs.freedomAge - inputs.currentAge} years to freedom
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
        <Field
          icon={Banknote}
          label="Current Monthly Expenses"
          hint={`= ${formatINR(inputs.monthlyExpenses * 12)} / year today`}
        >
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 num-font">
              ₹
            </span>
            <Input
              type="number"
              min={1000}
              step={1000}
              value={inputs.monthlyExpenses || ""}
              onChange={(e) => set({ monthlyExpenses: safeNum(e.target.value, 0) })}
              className="pl-8 h-12 bg-white/5 border-white/10 text-white num-font text-lg focus-visible:border-[#F97316] focus-visible:ring-[#F97316]/30"
              data-testid="input-monthly-expenses"
            />
          </div>
        </Field>

        <Field icon={Calendar} label="Current Age">
          <Input
            type="number"
            min={18}
            max={80}
            value={inputs.currentAge || ""}
            onChange={(e) => set({ currentAge: safeNum(e.target.value, 0) })}
            className="h-12 bg-white/5 border-white/10 text-white num-font text-lg focus-visible:border-[#F97316] focus-visible:ring-[#F97316]/30"
            data-testid="input-current-age"
          />
        </Field>

        <Field icon={Flag} label="Freedom Age">
          <Input
            type="number"
            min={inputs.currentAge + 1}
            max={85}
            value={inputs.freedomAge || ""}
            onChange={(e) => set({ freedomAge: safeNum(e.target.value, 0) })}
            className="h-12 bg-white/5 border-white/10 text-white num-font text-lg focus-visible:border-[#F97316] focus-visible:ring-[#F97316]/30"
            data-testid="input-freedom-age"
          />
        </Field>

        <Field
          icon={TrendingUp}
          label="Expected Annual Return"
          hint="Equity SIP long-term average is typically 11–13%"
        >
          <div className="flex items-center gap-4">
            <Slider
              value={[inputs.annualReturn * 100]}
              onValueChange={(v) => set({ annualReturn: v[0] / 100 })}
              min={8}
              max={13}
              step={0.5}
              className="flex-1"
              data-testid="slider-annual-return"
            />
            <div className="w-20 text-right font-heading text-lg font-semibold text-[#F97316] num-font">
              {(inputs.annualReturn * 100).toFixed(1)}%
            </div>
          </div>
        </Field>

        <Field
          icon={Flame}
          label="Annual Inflation"
          hint="India's long-term inflation ranges 5–7%"
        >
          <div className="flex items-center gap-4">
            <Slider
              value={[inputs.annualInflation * 100]}
              onValueChange={(v) => set({ annualInflation: v[0] / 100 })}
              min={5}
              max={7}
              step={0.25}
              className="flex-1"
              data-testid="slider-annual-inflation"
            />
            <div className="w-20 text-right font-heading text-lg font-semibold text-[#F97316] num-font">
              {(inputs.annualInflation * 100).toFixed(2)}%
            </div>
          </div>
        </Field>

        <Field icon={Repeat} label="Annual SIP Step-up">
          <div className="grid grid-cols-4 gap-2" data-testid="stepup-toggle-group">
            {STEPUPS.map((pct) => {
              const active = Math.abs(inputs.stepupPct - pct) < 1e-9;
              return (
                <button
                  key={pct}
                  type="button"
                  onClick={() => set({ stepupPct: pct })}
                  className={`h-12 rounded-md border text-sm font-medium transition-colors num-font ${
                    active
                      ? "bg-[#F97316] border-[#F97316] text-white shadow-lg shadow-orange-500/20"
                      : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:border-white/20"
                  }`}
                  data-testid={`stepup-btn-${Math.round(pct * 100)}`}
                >
                  {Math.round(pct * 100)}%
                </button>
              );
            })}
          </div>
        </Field>
      </div>
    </section>
  );
}
